/**
 * DocumentModalApprovalChain
 * 
 * Displays approval chain progress within DocumentModal
 * Shows multi-level approval workflow with expandable steps
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Clock, CheckCircle, XCircle, UserPlus, ChevronDown, Search } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';

export interface ApprovalChainNode {
  level: number;
  approver_id?: number;
  approver_name?: string;
  approver_role?: string;
  status: 'pending' | 'approved' | 'rejected' | 'delegated';
  approved_at?: string;
  delegated_to?: number;
  delegated_to_name?: string;
  comments?: string;
}

export interface ApprovalChain {
  id: string;
  document_id: string;
  document_type: string;
  document_number: string;
  total_amount: number;
  currency: string;
  current_level: number;
  max_level: number;
  nodes: ApprovalChainNode[];
  created_at: string;
  updated_at: string;
}

export interface DocumentModalApprovalChainProps {
  document: {
    id: string;
    status: string;
    doc_type?: string;
    doc_number?: string;
  };
  approvalChain?: ApprovalChain;
  onApprove?: (stepId: string, notes?: string) => Promise<void>;
  onReject?: (stepId: string, reason: string) => Promise<void>;
  onDelegate?: (stepId: string, toUserId: number) => Promise<void>;
}

function SaveDraftPrompt() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  
  return (
    <div className={`text-sm p-3 rounded-lg border ${
      isDark ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'
    }`}>
      Save this document to enable approval workflow tracking.
    </div>
  );
}

export function DocumentModalApprovalChain({
  document,
  approvalChain,
  onApprove,
  onReject,
  onDelegate,
}: DocumentModalApprovalChainProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  // Don't show approval chain for draft documents
  if (document.status === 'draft') {
    return <SaveDraftPrompt />;
  }

  if (!approvalChain || approvalChain.nodes.length === 0) {
    return (
      <div className={`text-sm p-3 rounded-lg border ${
        isDark ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-600'
      }`}>
        No approval workflow configured for this document type.
      </div>
    );
  }

  const approvedCount = approvalChain.nodes.filter(n => n.status === 'approved').length;
  const progressPercent = (approvedCount / approvalChain.nodes.length) * 100;

  return (
    <div
      data-tour-id="document-approval-chain"
      className={`space-y-3 rounded-lg p-4 border ${
      isDark
        ? 'bg-blue-900/20 border-blue-800'
        : 'bg-blue-50 border-blue-200'
    }`}>
      {/* Progress bar */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Approval Progress</h4>
          <span className="text-xs text-gray-600 dark:text-gray-400">
            {approvedCount}/{approvalChain.nodes.length} steps completed
          </span>
        </div>
        <div className={`w-full rounded-full h-1.5 ${
          isDark ? 'bg-blue-800' : 'bg-blue-200'
        }`}>
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Approval steps */}
      <div className="space-y-2">
        {approvalChain.nodes.map((step, idx) => (
          <ApprovalStepRow
            key={step.level}
            step={step}
            index={idx}
            total={approvalChain.nodes.length}
            isExpanded={expandedStep === String(step.level)}
            onToggle={() => setExpandedStep(expandedStep === String(step.level) ? null : String(step.level))}
            onApprove={() => onApprove?.(String(step.level))}
            onReject={(reason) => onReject?.(String(step.level), reason)}
            onDelegate={(userId) => onDelegate?.(String(step.level), userId)}
          />
        ))}
      </div>
    </div>
  );
}

interface ApprovalStepRowProps {
  step: ApprovalChainNode;
  index: number;
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onDelegate: (userId: number) => void;
}

function ApprovalStepRow({
  step,
  index,
  total,
  isExpanded,
  onToggle,
  onApprove,
  onReject,
  onDelegate,
}: ApprovalStepRowProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showDelegateInput, setShowDelegateInput] = useState(false);
  const [delegateUserId, setDelegateUserId] = useState('');
  const [delegateSearch, setDelegateSearch] = useState('');
  const [delegateUsers, setDelegateUsers] = useState<Array<{ id: number; name: string; email: string; role: string }>>([]);
  const [delegateUsersLoading, setDelegateUsersLoading] = useState(false);
  const [selectedDelegateUser, setSelectedDelegateUser] = useState<{ id: number; name: string } | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isDelegating, setIsDelegating] = useState(false);

  // Fetch client users when the delegate picker is opened
  const loadDelegateUsers = useCallback(async () => {
    setDelegateUsersLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      const res = await fetch('/api/v1/users?limit=100&role=admin,owner,root,super_admin,client_admin,manager', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      const rawUsers = Array.isArray(json?.data) ? json.data : Array.isArray(json?.users) ? json.users : [];
      const users = rawUsers
        .map((u: unknown) => {
          const r = u && typeof u === 'object' ? u as Record<string, unknown> : null;
          if (!r) return null;
          const id = typeof r.id === 'number' ? r.id : Number(r.id);
          if (!Number.isFinite(id)) return null;
          const name = String(r.name || r.display_name || r.email || 'Unknown');
          const email = String(r.email || '');
          const role = String(r.role || '');
          return { id, name, email, role };
        })
        .filter((u: { id: number; name: string; email: string; role: string } | null): u is { id: number; name: string; email: string; role: string } => Boolean(u));
      setDelegateUsers(users);
    } catch {
      // non-fatal — user can still type an ID manually
    } finally {
      setDelegateUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showDelegateInput && delegateUsers.length === 0) {
      void loadDelegateUsers();
    }
  }, [showDelegateInput, delegateUsers.length, loadDelegateUsers]);

  const statusIcon = {
    pending: <Clock className="h-4 w-4 text-yellow-600" />,
    approved: <CheckCircle className="h-4 w-4 text-green-600" />,
    rejected: <XCircle className="h-4 w-4 text-red-600" />,
    delegated: <UserPlus className="h-4 w-4 text-blue-600" />,
  }[step.status];

  const statusBadge = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    delegated: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  }[step.status];

  return (
    <div
      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
        isDark 
          ? 'border-gray-700 hover:bg-gray-800' 
          : 'border-gray-200 hover:bg-gray-100'
      }`}
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {statusIcon}
          <div>
            <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
              Step {index + 1}: {step.approver_role || 'Approver'}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {step.approver_name || 'Unassigned'}{' '}
              {step.delegated_to_name && `(delegated to ${step.delegated_to_name})`}
            </div>
          </div>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge}`}>
            {step.status}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform text-gray-500 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </div>

      {isExpanded && step.status === 'pending' && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={async () => {
              setIsApproving(true);
              try {
                await onApprove();
              } finally {
                setIsApproving(false);
              }
            }}
            disabled={isApproving}
            className="w-full px-3 py-2 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {isApproving ? 'Approving...' : '✓ Approve'}
          </button>
          {!showRejectInput ? (
            <button
              onClick={() => setShowRejectInput(true)}
              className="w-full px-3 py-2 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
            >
              ✕ Reject
            </button>
          ) : (
            <div className="space-y-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={2}
                className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-red-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    if (!rejectReason.trim() || isRejecting) return;
                    setIsRejecting(true);
                    try {
                      await onReject(rejectReason.trim());
                      setRejectReason('');
                      setShowRejectInput(false);
                    } finally {
                      setIsRejecting(false);
                    }
                  }}
                  disabled={!rejectReason.trim() || isRejecting}
                  className="flex-1 px-3 py-2 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {isRejecting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectInput(false);
                    setRejectReason('');
                  }}
                  className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {!showDelegateInput ? (
            <button
              onClick={() => setShowDelegateInput(true)}
              className="w-full px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
            >
              ↗ Delegate
            </button>
          ) : (
            <div className="space-y-2">
              {selectedDelegateUser ? (
                <div className="flex items-center justify-between rounded border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 px-2 py-1.5">
                  <span className="text-xs text-gray-900 dark:text-white">
                    {selectedDelegateUser.name}
                    <span className="text-gray-500 dark:text-gray-400 ml-1">(ID: {selectedDelegateUser.id})</span>
                  </span>
                  <button
                    onClick={() => {
                      setSelectedDelegateUser(null);
                      setDelegateSearch('');
                      setDelegateUserId('');
                    }}
                    className="text-xs text-gray-500 hover:text-red-500"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={delegateSearch}
                      onChange={(e) => setDelegateSearch(e.target.value)}
                      placeholder={delegateUsersLoading ? 'Loading users...' : 'Search by name or email...'}
                      disabled={delegateUsersLoading}
                      className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-7 pr-2 py-1 text-xs text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </div>
                  {delegateSearch.length >= 1 && !selectedDelegateUser && (
                    <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                      {delegateUsersLoading ? (
                        <div className="px-2 py-1.5 text-xs text-gray-500">Loading...</div>
                      ) : delegateUsers.length === 0 ? (
                        <div className="px-2 py-1.5 text-xs text-gray-500">No users found</div>
                      ) : (
                        delegateUsers
                          .filter((u) => {
                            const q = delegateSearch.toLowerCase();
                            return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
                          })
                          .slice(0, 8)
                          .map((u) => (
                            <button
                              key={u.id}
                              onClick={() => {
                                setSelectedDelegateUser({ id: u.id, name: u.name });
                                setDelegateUserId(String(u.id));
                                setDelegateSearch('');
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">{u.name}</div>
                              <div className="text-gray-500 dark:text-gray-400">{u.email} · {u.role}</div>
                            </button>
                          ))
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    const uid = parseInt(delegateUserId, 10);
                    if (Number.isNaN(uid) || isDelegating) return;
                    setIsDelegating(true);
                    try {
                      await onDelegate(uid);
                      setDelegateUserId('');
                      setSelectedDelegateUser(null);
                      setDelegateSearch('');
                      setShowDelegateInput(false);
                    } finally {
                      setIsDelegating(false);
                    }
                  }}
                  disabled={!delegateUserId.trim() || isDelegating}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isDelegating ? 'Delegating...' : 'Confirm Delegate'}
                </button>
                <button
                  onClick={() => {
                    setShowDelegateInput(false);
                    setDelegateUserId('');
                    setSelectedDelegateUser(null);
                    setDelegateSearch('');
                  }}
                  className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isExpanded && step.comments && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-medium">Notes:</span> {step.comments}
          </div>
        </div>
      )}
    </div>
  );
}
